import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Rating,
  Slider,
  Typography,
  Box,
  Checkbox,
  FormControlLabel,
  Snackbar,
  Alert,
} from '@mui/material';
import FilledButton from './FilledButton';
import OutlinedButton from './OutlinedButton';
import DifficultyGauge from './DifficultyGauge';
import { updateReview } from '../services/api';

const gradeMarks = [
  { value: 4.0, label: 'A' },
  { value: 3.7, label: 'A-' },
  { value: 3.3, label: 'B+' },
  { value: 3.0, label: 'B' },
  { value: 2.7, label: 'B-' },
  { value: 2.3, label: 'C+' },
  { value: 2.0, label: 'C' },
  { value: 1.7, label: 'C-' },
  { value: 1.3, label: 'D+' },
  { value: 1.0, label: 'D' },
  { value: 0.7, label: 'D-' },
  { value: 0.0, label: 'F' },
];

const EditReviewDialog = ({ open, onClose, review, onSaved, onSuccess }) => {
  const [rating, setRating] = useState(0);
  const [difficulty, setDifficulty] = useState(0);
  const [gradeReceived, setGradeReceived] = useState('');
  const [agreeToShowGrade, setAgreeToShowGrade] = useState(false);
  const [content, setContent] = useState('');
  const [saving, setSaving] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');

  useEffect(() => {
    if (!open) return;
    setRating(review?.rating || 0);
    setDifficulty(review?.difficulty_level || 0);
    if (review && review.grade_received !== null && review.grade_received !== undefined) {
      setAgreeToShowGrade(true);
      setGradeReceived(String(review.grade_received));
    } else {
      setAgreeToShowGrade(false);
      setGradeReceived('');
    }
    setContent(review?.content || '');
  }, [open, review]);

  const handleSave = async () => {
    if (!review) return;
    setSaving(true);
    try {
      const payload = {
        content,
        rating,
        difficulty_level: parseInt(difficulty, 10),
        grade_received: agreeToShowGrade && gradeReceived !== '' ? parseFloat(gradeReceived) : null,
      };
      const updated = await updateReview(review.id, payload);
      if (onSaved) onSaved(updated);
      const msg = 'Review updated';
      if (onSuccess) onSuccess(msg); else setSuccessMessage(msg);
      onClose();
    } catch (err) {
      console.error('Failed to update review', err);
    } finally {
      setSaving(false);
    }
  };

  const hasChanges = () => {
    if (!review) return false;
    const originalGrade =
      review.grade_received !== null && review.grade_received !== undefined
        ? String(review.grade_received)
        : '';
    return (
      rating !== (review.rating || 0) ||
      difficulty !== (review.difficulty_level || 0) ||
      content !== (review.content || '') ||
      (agreeToShowGrade ? gradeReceived : '') !== originalGrade ||
      (!agreeToShowGrade && originalGrade !== '')
    );
  };

  return (
    <>
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>Edit Review</DialogTitle>
      <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
        {review && (
          <Box sx={{ mb: 1 }}>
            <Typography variant="body2">{review.professor_name}</Typography>
            <Typography variant="body2">{review.course_name}</Typography>
            {review.semester && (
              <Typography variant="body2">
                {review.semester.term} {review.semester.year}
              </Typography>
            )}
          </Box>
        )}
        <Box>
          <Typography variant="subtitle2" fontWeight="bold" sx={{ mb: 0.5 }}>
            Rating
          </Typography>
          <Rating value={rating} onChange={(e, v) => setRating(v)} precision={1} max={5} />
        </Box>
        <Box>
          <Typography variant="subtitle2" fontWeight="bold" sx={{ mb: -0.5 }}>
            Difficulty Level
          </Typography>
          <DifficultyGauge value={difficulty} onChange={setDifficulty} size={100} />
        </Box>
        <Box>
          <Typography variant="subtitle2" fontWeight="bold" sx={{ mb: 0.5 }}>
            Grade Received (optional)
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Slider
              min={0}
              max={4}
              step={0.1}
              marks={gradeMarks}
              value={gradeReceived === '' ? 0 : parseFloat(gradeReceived)}
              onChange={(e, val) => setGradeReceived(val.toFixed(1))}
              valueLabelDisplay="auto"
              disabled={!agreeToShowGrade}
              sx={{ flexGrow: 1 }}
            />
            <TextField
              value={gradeReceived}
              onChange={(e) => setGradeReceived(e.target.value)}
              size="small"
              type="number"
              disabled={!agreeToShowGrade}
              inputProps={{ min: 0, max: 4, step: 0.1 }}
              sx={{ width: 80 }}
            />
          </Box>
          <FormControlLabel
            control={
              <Checkbox
                checked={agreeToShowGrade}
                onChange={(e) => {
                  setAgreeToShowGrade(e.target.checked);
                  if (!e.target.checked) setGradeReceived('');
                }}
              />
            }
            label="Display grade with review"
          />
        </Box>
        <div>
          <Typography variant="subtitle2" fontWeight="bold" sx={{ mb: 0.5 }}>
            Review Content
          </Typography>
          <TextField
            multiline
            rows={4}
            value={content}
            onChange={(e) => setContent(e.target.value)}
            fullWidth
          />
        </div>
      </DialogContent>
      <DialogActions>
        <OutlinedButton onClick={onClose} text="Cancel" color="info" />
        <FilledButton onClick={handleSave} disabled={saving || !hasChanges()} text="Save" color="info" />
      </DialogActions>
    </Dialog>
    <Snackbar
      open={!!successMessage}
      autoHideDuration={3000}
      onClose={() => setSuccessMessage('')}
      anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
    >
      <Alert onClose={() => setSuccessMessage('')} severity="success" sx={{ width: '100%', fontWeight: 'bold' }}>
        {successMessage}
      </Alert>
    </Snackbar>
    </>
  );
};

export default EditReviewDialog;
